import type { Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Link from '@tiptap/extension-link';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Highlight from '@tiptap/extension-highlight';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Youtube from '@tiptap/extension-youtube';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { lowlight } from './lowlight';
import { NulaLinkExtension } from './nula-link-extension';
import { EmojiExtension } from './emoji-extension';
import { CalloutExtension } from './callout-extension';
import { CollapsibleExtensions } from './collapsible-extension';
import { CommentMark } from './comment-extension';
import { ReadOnlyGuard } from './read-only-guard-extension';

export function readOnlyContentExtensions(): Extensions {
  return [
    StarterKit.configure({ undoRedo: false, codeBlock: false, trailingNode: false }),
    CodeBlockLowlight.configure({ lowlight }),
    Link,
    TaskList,
    TaskItem.configure({ nested: true }),
    // Schema parity with the editable RichTextEditor — both bind the same
    // Yjs doc, so the viewer MUST register every node/mark the editor can
    // emit, or it errors/drops content when rendering a doc that uses them.
    Highlight.configure({ multicolor: true }),
    TableKit.configure({ table: { resizable: false } }),
    TextStyle,
    Color,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Image,
    Subscript,
    Superscript,
    Youtube,
    NulaLinkExtension,
    EmojiExtension,
    CalloutExtension,
    CommentMark,
    ...CollapsibleExtensions,
  ];
}

/**
 * The viewer's extensions for a doc bound to Yjs: the read-only content plus
 * the guard that keeps it from writing to the shared doc. Not for a preview
 * that renders with setContent (MarkdownSectionPreview), which the guard
 * would block.
 */
export function collaborativeViewerExtensions(): Extensions {
  return [...readOnlyContentExtensions(), ReadOnlyGuard];
}
